import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import NoteAddRoundedIcon from '@mui/icons-material/NoteAddRounded';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  styled,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { includes, isEmpty, orderBy, size } from 'es-toolkit/compat';
import React, { useState } from 'react';
import { BooleanParam, useQueryParam, withDefault } from 'use-query-params';

import { DialogWithCloseButton } from 'components/atoms/Dialog/DialogWithCloseButton';
import { NON_SPECIFIC_STUDY_VALUES } from 'components/SearchFilters/hooks/useMainFilters';
import { Permission } from 'interfaces/permissionOption';
import { useAnnotationAssignments } from 'utils/useAnnotationAssignments';
import { usePermissions } from 'utils/usePermissions';
import { CreateAnnotationAssignmentDialog } from '../AnnotationAssignment/CreateAnnotationAssignmentDialog';
import AnnotationAssignmentQuickNavigator, {
  AssignmentFilterMode,
  buildSearchableAnnotationAssignments,
  filterSearchableAnnotationAssignments,
} from './AnnotationAssignmentQuickNavigator';
import { AnnotationAssignmentTaggingDialogWithContext } from './AnnotationAssignmentTaggingDialog/AnnotationAssignmentTaggingDialogWithContext';
import TrainingDialog from './TrainingDialog';

const PlaceholderBox = styled(Box)(() => ({
  height: 300,
  paddingX: 10,
  paddingTop: 3,
}));

interface AnnotationAssignmentViewProps {
  studyId: string;
}

const contentPaddingX = { xs: 2, md: 6, xl: 10 };

const AnnotationAssignmentsView: React.FC<AnnotationAssignmentViewProps> = ({ studyId }) => {
  const { hasPermission } = usePermissions();
  const isSpecificStudySelected = studyId && !includes(NON_SPECIFIC_STUDY_VALUES, studyId.toString());
  const canViewAnnotationAssignments = hasPermission(Permission.ViewAnnotationsList) && isSpecificStudySelected;
  const canCreateAnnotationAssignment = hasPermission(Permission.CreateAnnotationAssignment);
  const [createAssignmentDialogOpen, setCreateAssignmentDialogOpen] = useState(false);
  const [assignmentFilterMode, setAssignmentFilterMode] = useState<AssignmentFilterMode>('all');
  const [assignmentSearchQuery, setAssignmentSearchQuery] = useState('');
  const [showLegacyAssignments, setShowLegacyAssignments] = useQueryParam(
    'showLegacyAssignments',
    withDefault(BooleanParam, true)
  );
  const [showOnlySlidesOfStudy, setShowOnlySlidesOfStudy] = useQueryParam(
    'showOnlySlidesOfStudy',
    withDefault(BooleanParam, true)
  );
  const deferredShowLegacyAssignments = React.useDeferredValue(showLegacyAssignments);
  const deferredShowOnlySlidesOfStudy = React.useDeferredValue(showOnlySlidesOfStudy);
  const {
    data: studyAnnotationAssignmentResponse,
    isLoading,
    isFetched,
    isError,
  } = useAnnotationAssignments(studyId, deferredShowLegacyAssignments, deferredShowOnlySlidesOfStudy);

  const sortedStudyAnnotationAssignments = React.useMemo(
    () => orderBy(studyAnnotationAssignmentResponse?.annotationAssignments, 'annotationAssignmentId', 'desc'),
    [studyAnnotationAssignmentResponse?.annotationAssignments]
  );
  const searchableStudyAnnotationAssignments = React.useMemo(
    () => buildSearchableAnnotationAssignments(sortedStudyAnnotationAssignments),
    [sortedStudyAnnotationAssignments]
  );
  const assignmentCount = size(sortedStudyAnnotationAssignments);
  const deferredAssignmentSearchQuery = React.useDeferredValue(assignmentSearchQuery);
  const trimmedAssignmentSearchQuery = assignmentSearchQuery.trim();
  const deferredTrimmedAssignmentSearchQuery = deferredAssignmentSearchQuery.trim();
  const filteredStudyAnnotationAssignments = React.useMemo(
    () =>
      filterSearchableAnnotationAssignments(
        searchableStudyAnnotationAssignments,
        deferredTrimmedAssignmentSearchQuery,
        assignmentFilterMode
      ),
    [assignmentFilterMode, deferredTrimmedAssignmentSearchQuery, searchableStudyAnnotationAssignments]
  );
  const visibleAssignmentCount = size(filteredStudyAnnotationAssignments);
  const hasSearchOrFilter = Boolean(trimmedAssignmentSearchQuery) || assignmentFilterMode !== 'all';

  const canExecuteModelTraining = hasPermission(Permission.ExecuteModelTraining);
  const [showTrainingDialog, setShowTrainingDialog] = useQueryParam('showTrainingDialog', BooleanParam);
  const canTagAnnotationAssignments = hasPermission(Permission.EditAnnotationAssignmentTags);
  const [showAnnotationAssignmentTaggingDialog, setShowAnnotationAssignmentTaggingDialog] = useQueryParam(
    'showAnnotationAssignmentTaggingDialog',
    BooleanParam
  );

  const clearAssignmentFilters = () => {
    setAssignmentSearchQuery('');
    setAssignmentFilterMode('all');
  };

  return !isSpecificStudySelected ? (
    <PlaceholderBox>
      <Typography variant="h4">Please select a specific study to view annotation assignments</Typography>
    </PlaceholderBox>
  ) : !canViewAnnotationAssignments ? (
    // if some how the user got to this page without the correct permissions
    <PlaceholderBox>
      <Typography variant="h4">You do not have permission to view this page</Typography>
    </PlaceholderBox>
  ) : isError ? (
    <PlaceholderBox>
      <Typography variant="h4">Failed to load annotation assignments, please contact support</Typography>
    </PlaceholderBox>
  ) : (
    <>
      <Box sx={{ px: contentPaddingX, py: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Grid container spacing={3} alignItems="flex-start" justifyContent="space-between">
                  <Grid item xs={12} lg={4}>
                    <Typography variant="h3">Annotation assignments</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 760 }}>
                      Create assignments, find the right one quickly, and open training preparation tools from the same
                      place.
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                      <Chip size="small" color="secondary" label={`${assignmentCount} assignments`} />
                      {hasSearchOrFilter && (
                        <Chip size="small" variant="outlined" label={`${visibleAssignmentCount} shown`} />
                      )}
                      <Chip
                        size="small"
                        variant="outlined"
                        label={showOnlySlidesOfStudy ? 'Study slides only' : 'All matching slides'}
                      />
                      {showLegacyAssignments && (
                        <Chip size="small" variant="outlined" label="Including assignments from outside this study" />
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} lg={8}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                        alignItems: 'stretch',
                      }}
                    >
                      {(canExecuteModelTraining || canTagAnnotationAssignments) && (
                        <Box
                          sx={{
                            flex: canCreateAnnotationAssignment ? '1 1 58%' : '1 1 100%',
                            minWidth: 0,
                            p: 2,
                          }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                            }}
                          >
                            <Typography variant="h4">Training and tagging tools</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, mb: 2 }}>
                              Use these actions when preparing training inputs or standardizing assignment tags.
                            </Typography>
                            <Grid container spacing={1.5}>
                              {canExecuteModelTraining && (
                                <Grid item xs={12} sm="auto">
                                  <Button
                                    fullWidth
                                    variant="outlined"
                                    startIcon={<SchoolOutlinedIcon />}
                                    onClick={() => setShowTrainingDialog(true)}
                                  >
                                    Create training classes config
                                  </Button>
                                </Grid>
                              )}
                              {canTagAnnotationAssignments && (
                                <Grid item xs={12} sm="auto">
                                  <Button
                                    fullWidth
                                    variant="outlined"
                                    startIcon={<LocalOfferOutlinedIcon />}
                                    onClick={() => setShowAnnotationAssignmentTaggingDialog(true)}
                                  >
                                    Manage assignment tags
                                  </Button>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        </Box>
                      )}
                      {canCreateAnnotationAssignment && (canExecuteModelTraining || canTagAnnotationAssignments) && (
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{ display: { xs: 'none', md: 'block' }, alignSelf: 'stretch' }}
                        />
                      )}
                      {canCreateAnnotationAssignment && (canExecuteModelTraining || canTagAnnotationAssignments) && (
                        <Divider sx={{ display: { xs: 'block', md: 'none' } }} />
                      )}
                      {canCreateAnnotationAssignment && (
                        <Box
                          sx={{
                            flex: '1 1 42%',
                            minWidth: 0,
                            p: 2,
                            display: 'flex',
                          }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                              width: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <Typography variant="h4">Start a new assignment</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, mb: 2 }}>
                              Add a fresh class set for this study when you need a new annotation track.
                            </Typography>
                            <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'flex-start' }}>
                              <Button
                                color="primary"
                                variant="contained"
                                startIcon={<NoteAddRoundedIcon />}
                                onClick={() => setCreateAssignmentDialogOpen(true)}
                              >
                                New annotation assignment
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
              <Divider />
              <CardContent sx={{ pt: 2.5 }}>
                <Grid container spacing={2.5} alignItems="flex-start">
                  <Grid item xs={12} lg={5}>
                    <TextField
                      type="search"
                      value={assignmentSearchQuery}
                      onChange={(event) => setAssignmentSearchQuery(event.target.value)}
                      placeholder="Search by assignment name, ID, class, marker, or tag"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchRoundedIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <ToggleButtonGroup
                      exclusive
                      value={assignmentFilterMode}
                      onChange={(_event, nextValue: AssignmentFilterMode | null) => {
                        if (nextValue) {
                          setAssignmentFilterMode(nextValue);
                        }
                      }}
                      size="small"
                      color="secondary"
                    >
                      <ToggleButton value="all">All</ToggleButton>
                      <ToggleButton value="study">Study</ToggleButton>
                      <ToggleButton value="global">Global</ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Narrow the list to study-specific assignments or shared global ones.
                    </Typography>
                    {hasSearchOrFilter && visibleAssignmentCount !== assignmentCount && (
                      <Box sx={{ mt: 1.5 }}>
                        <Button variant="text" onClick={clearAssignmentFilters} sx={{ px: 0 }}>
                          Clear search and filters
                        </Button>
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12} lg={3}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Choose whether to limit the list to slides in this study and whether to include assignments from
                      outside this study that already exist on those slides.
                    </Typography>
                    <Grid container direction="column">
                      <Grid item>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={showOnlySlidesOfStudy}
                              onChange={(e) => setShowOnlySlidesOfStudy(e.target.checked)}
                            />
                          }
                          label="Limit results to slides in this study"
                        />
                      </Grid>
                      <Grid item>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={showLegacyAssignments}
                              onChange={(e) => setShowLegacyAssignments(e.target.checked)}
                            />
                          }
                          label="Also include assignments from outside this study on those slides"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {isFetched && isEmpty(sortedStudyAnnotationAssignments) ? (
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ py: 6, textAlign: 'center' }}>
                  <Typography variant="h4">No annotation assignments yet</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 560, mx: 'auto' }}>
                    Create the first assignment for this study to define annotation classes, distribute annotation work,
                    and prepare downstream training or tagging actions.
                  </Typography>
                  {canCreateAnnotationAssignment && (
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                      <Button
                        color="primary"
                        variant="contained"
                        startIcon={<NoteAddRoundedIcon />}
                        onClick={() => setCreateAssignmentDialogOpen(true)}
                      >
                        Create annotation assignment
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ) : !isLoading && isEmpty(filteredStudyAnnotationAssignments) ? (
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ py: 6, textAlign: 'center' }}>
                  <Typography variant="h4">No assignments match this search</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 560, mx: 'auto' }}>
                    Try a different assignment name, ID, class, marker, or tag, or clear the current filter to see the
                    full list again.
                  </Typography>
                  {hasSearchOrFilter && (
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                      <Button variant="outlined" onClick={clearAssignmentFilters}>
                        Clear search and filters
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ) : (
            <AnnotationAssignmentQuickNavigator
              studyId={studyId}
              annotationAssignments={filteredStudyAnnotationAssignments}
              isLoading={isLoading}
              fetchOnlySlidesOfStudy={deferredShowOnlySlidesOfStudy}
              withLegacy={deferredShowLegacyAssignments}
            />
          )}
        </Grid>
      </Box>

      <CreateAnnotationAssignmentDialog
        open={createAssignmentDialogOpen}
        onClose={() => setCreateAssignmentDialogOpen(false)}
      />

      {showTrainingDialog && (
        <DialogWithCloseButton
          fullScreen
          header="Create Training Classes Config"
          onClose={() => setShowTrainingDialog(false)}
        >
          <TrainingDialog
            studyId={studyId}
            studyAnnotationAssignments={studyAnnotationAssignmentResponse?.annotationAssignments}
          />
        </DialogWithCloseButton>
      )}
      {showAnnotationAssignmentTaggingDialog && (
        <DialogWithCloseButton
          header="Tag annotation assignments"
          onClose={() => setShowAnnotationAssignmentTaggingDialog(false)}
        >
          <AnnotationAssignmentTaggingDialogWithContext studyId={studyId} />
        </DialogWithCloseButton>
      )}
    </>
  );
};

export default AnnotationAssignmentsView;
